/**
 * @changelog
 *
 * 2026-03-11 (0.1.0) (jhoward)
 *   - Add App\Helpers\getWebAuthnChallengeResponse() to process WebAuthn credential responses for authentication and enrollment.
 *   - Includes internal functions for both authentication and enrollment flows, returns structured result.
 *
 * 2026-03-24 (0.1.1) (jhoward)
 *   - Use peppermillCustomFetchProxy instead of peppermillCustomFetch for challenge response.
 *
 * 2026-04-29 (0.2.0) (jhoward)
 *   - Accept rememberDevice parameter for authentication flow and forward to server.
 *
 * 2026-10-10 (0.3.0) (jhoward)
 *   - Return the passkey and the user the server now answers enrollment with, and call it a passkey.
 */

import type { FetchResponse } from '@framework/defs/framework';
import { arrayBufferToBase64 } from '@framework/helpers';
import type { Passkey } from '@/defs/Passkey';
import type { RequestType } from '@/helpers/getWebAuthnChallenge';
import peppermillCustomFetchProxy from '@/helpers/peppermillCustomFetchProxy';
import type { LoginResponseData } from '@/views/Auth/Login.vue';
import { verify } from '@rss/routes/login/two-factor/webauthn';

export interface WebAuthnChallengeOutput extends LoginResponseData {
  success: boolean;
  message: string;
  honeypot?: string;
  twoFactorAuthRequired?: boolean;
  backup_codes?: string[];
  passkey?: Passkey;
}

interface WebAuthnEnrollmentVerifyResponse extends FetchResponse {
  data: LoginResponseData & {
    backup_codes?: string[];
    passkey?: Passkey;
  };
}

export default async function getWebAuthnChallengeResponse(
  requestType: RequestType,
  credential: PublicKeyCredential,
  rememberDevice: boolean = false,
): Promise<WebAuthnChallengeOutput> {
  if ('authentication' === requestType) {
    return await getChallengeResponseForAuthentication(credential, rememberDevice);
  }

  if ('enrollment' === requestType) {
    return await getChallengeResponseForEnrollment(credential);
  }

  return {
    success: false,
    message: 'Invalid request type.',
  };
}

async function getChallengeResponseForAuthentication(
  credential: PublicKeyCredential,
  rememberDevice: boolean = false,
): Promise<WebAuthnChallengeOutput> {
  const credentialResponse = credential.response as AuthenticatorAssertionResponse;
  const response = await peppermillCustomFetchProxy<WebAuthnEnrollmentVerifyResponse>(
    {
      id: credential.rawId ? arrayBufferToBase64(credential.rawId) : null,
      clientDataJSON: credentialResponse.clientDataJSON ? arrayBufferToBase64(credentialResponse.clientDataJSON) : null,
      authenticatorData: credentialResponse.authenticatorData
        ? arrayBufferToBase64(credentialResponse.authenticatorData)
        : null,
      signature: credentialResponse.signature ? arrayBufferToBase64(credentialResponse.signature) : null,
      userHandle: credentialResponse.userHandle ? arrayBufferToBase64(credentialResponse.userHandle) : null,
      remember_device: rememberDevice ? 1 : 0,
    },
    {
      route: verify({ requestType: 'authentication' }),
    },
  );
  if (200 === response.status) {
    return {
      success: true,
      message: response.message?.length ? response.message : 'Signed in with your passkey.',
      user: response.data.user,
      csrfToken: response.data.csrfToken,
      redirect: response.data.redirect,
      twoFactorAuthRequired: response.data.twoFactorAuthRequired,
      honeypot: response.data.honeypot,
    };
  }
  return {
    success: false,
    message: response.message?.length ? response.message : 'An error occurred while validating the challenge.',
  };
}

async function getChallengeResponseForEnrollment(credential: PublicKeyCredential): Promise<WebAuthnChallengeOutput> {
  const credentialResponse = credential.response as AuthenticatorAttestationResponse;
  const response = await peppermillCustomFetchProxy<WebAuthnEnrollmentVerifyResponse>(
    {
      transports: credentialResponse.getTransports ? credentialResponse.getTransports() : null,
      clientDataJSON: credentialResponse.clientDataJSON ? arrayBufferToBase64(credentialResponse.clientDataJSON) : null,
      attestationObject: credentialResponse.attestationObject
        ? arrayBufferToBase64(credentialResponse.attestationObject)
        : null,
    },
    {
      route: verify({ requestType: 'enrollment' }),
    },
  );

  if (200 === response.status) {
    return {
      success: true,
      message: response.message?.length ? response.message : 'Your passkey has been added.',
      backup_codes: response.data.backup_codes || [],
      passkey: response.data.passkey,
      user: response.data.user,
    };
  }
  return {
    success: false,
    message: response.message?.length ? response.message : 'An error occurred while validating the challenge.',
  };
}
